import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../router/routes';
import { Tooltip } from '@ecargo/web-components';
import { Button } from '@hangar/web-components/core/Button';
import { CloseOutlined } from '@hangar/react-icons/core/interaction/CloseOutlined';
import { useTranslation } from '../../hooks/i18n';
import { useAssets } from '../../hooks/utils';
import { useDocumentHeadTitle } from '../../hooks/utils/useDocumentHeadTitle';
import { LATAM_CARGO_PUBLIC_PAGE_TITLES } from '../../utils/documentHeadTitle';
import { unsubscribe } from '../../services/api/unsubscribeService';
import { validateUnsubscribeEmail } from '../../utils/unsubscribe/validateEmail';
import { UNSUBSCRIBE_REASONS, UnsubscribeReason } from './types';
import * as S from './UnsubscribeLandingPage.styles';

const REASON_I18N_KEYS: Record<UnsubscribeReason, string> = {
  TOO_MANY_EMAILS: 'unsubscribe.reason.tooMany',
  NOT_RELEVANT: 'unsubscribe.reason.notRelevant',
  WRONG_PERSON: 'unsubscribe.reason.wrongPerson',
  OTHER: 'unsubscribe.reason.other',
};

export const UnsubscribeLandingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { assets } = useAssets();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    i18n.changeLanguage('en');
  }, []);

  useDocumentHeadTitle(LATAM_CARGO_PUBLIC_PAGE_TITLES.unsubscribe);

  const initialEmail = searchParams.get('email')?.trim() || '';
  const decodedEmail = initialEmail ? decodeURIComponent(initialEmail) : '';

  const [email, setEmail] = useState(decodedEmail);
  const [reason, setReason] = useState<UnsubscribeReason | ''>('');
  const [emailsToNotify, setEmailsToNotify] = useState<string[]>([]);
  const [emailToAdd, setEmailToAdd] = useState('');
  const [emailToAddError, setEmailToAddError] = useState(false);
  const [otherReasonText, setOtherReasonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (decodedEmail) setEmail(decodedEmail);
  }, [decodedEmail]);

  const isValid = validateUnsubscribeEmail(email) && reason !== '';
  const isDisabled = !isValid || loading;

  const handleAddEmail = () => {
    const trimmed = emailToAdd.trim();
    if (!trimmed) return setEmailToAddError(false);
    if (!validateUnsubscribeEmail(trimmed)) {
      setEmailToAddError(true);
      return;
    }
    setEmailToAddError(false);
    const normalized = trimmed.toLowerCase();
    if (emailsToNotify.includes(normalized)) return;
    setEmailsToNotify([...emailsToNotify, normalized]);
    setEmailToAdd('');
    setError(null);
  };

  const handleRemoveEmail = (toRemove: string) => {
    setEmailsToNotify(emailsToNotify.filter((e) => e !== toRemove));
  };

  const buildListEmails = (): string[] | undefined => {
    if (reason === UNSUBSCRIBE_REASONS.WRONG_PERSON && emailsToNotify.length > 0) {
      return emailsToNotify;
    }
    return undefined;
  };

  const buildReason = (): string | undefined => {
    if (!reason) return undefined;
    if (reason === UNSUBSCRIBE_REASONS.OTHER && otherReasonText.trim()) {
      return otherReasonText.trim();
    }
    return reason;
  };

  const handleConfirm = async () => {
    if (isDisabled) return;
    setLoading(true);
    setError(null);
    try {
      const listEmails = buildListEmails();
      const reasonToSend = buildReason();
      await unsubscribe(email.trim(), reasonToSend, listEmails);
      navigate(ROUTES.UNSUBSCRIBE_THANK_YOU);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const showEmailsToNotify = reason === UNSUBSCRIBE_REASONS.WRONG_PERSON;
  const showTellUsMore = reason === UNSUBSCRIBE_REASONS.OTHER;

  return (
    <S.Container>
      <S.LogoContainer>
        <S.Logo src={assets.logos.latamCargo} alt="LATAM CARGO" />
      </S.LogoContainer>
      <S.Card>
        <S.Title as="h1">
          {t('unsubscribe.title')}
        </S.Title>

        <S.FormGroup>
          <S.FormGroupContent>
            <S.Label htmlFor="unsubscribe-email-input">{t('unsubscribe.emailLabel')}</S.Label>
            <S.Input
              id="unsubscribe-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              data-testid="unsubscribe-email-input"
            />
            <S.HelperText>{t('unsubscribe.helperText')}</S.HelperText>
          </S.FormGroupContent>
        </S.FormGroup>

        <S.ReasonCard $hasExpandableContent={showEmailsToNotify || showTellUsMore}>
          <S.ReasonTitle>
            {t('unsubscribe.reasonTitle')}
          </S.ReasonTitle>
          <S.RadioGroup>
            {(Object.keys(UNSUBSCRIBE_REASONS) as Array<keyof typeof UNSUBSCRIBE_REASONS>).map(
              (key) => {
                const value = UNSUBSCRIBE_REASONS[key];
                const isLastBeforeEmails = value === UNSUBSCRIBE_REASONS.WRONG_PERSON;
                return (
                  <React.Fragment key={value}>
                    <S.RadioOption>
                      <input
                        type="radio"
                        id={value}
                        name="reason"
                        value={value}
                        checked={reason === value}
                        onChange={() => {
                          setReason(value);
                          setEmailsToNotify([]);
                          setEmailToAdd('');
                          setEmailToAddError(false);
                          setOtherReasonText('');
                          setError(null);
                        }}
                        data-testid={`unsubscribe-reason-${value}`}
                      />
                      <label htmlFor={value}>{t(REASON_I18N_KEYS[value])}</label>
                    </S.RadioOption>
                    {isLastBeforeEmails && showEmailsToNotify && (
                      <S.EmailsToNotifySection>
                        <S.Label htmlFor="emails-to-notify-input">{t('unsubscribe.emailsToNotifyLabel')}</S.Label>
                        <S.AddEmailRow>
                          <S.AddEmailInputWrapper>
                            <S.AddEmailInput
                              id="emails-to-notify-input"
                              type="email"
                              value={emailToAdd}
                              onChange={(e) => {
                                setEmailToAdd(e.target.value);
                                setEmailToAddError(false);
                              }}
                              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                              placeholder={t('unsubscribe.emailsToNotifyPlaceholder')}
                              $hasError={emailToAddError}
                              $hasErrorIcon={emailToAddError}
                              data-testid="emails-to-notify-input"
                            />
                            {emailToAddError && (
                              <S.InputErrorIcon aria-hidden>!</S.InputErrorIcon>
                            )}
                          </S.AddEmailInputWrapper>
                          <S.EmailsToNotifyHelperText $hasError={emailToAddError}>
                            {emailToAddError ? t('unsubscribe.emailsToNotifyError') : t('unsubscribe.emailsToNotifyHelper')}
                          </S.EmailsToNotifyHelperText>
                          <S.StyledAddButton>
                            <Button
                              label={t('unsubscribe.addButton')}
                              variant="secondary"
                              onClick={handleAddEmail}
                              data-testid="add-email-button"
                            />
                          </S.StyledAddButton>
                        </S.AddEmailRow>
                        {emailsToNotify.length > 0 && (
                          <S.ChipList>
                            {emailsToNotify.map((e) => (
                              <S.Chip key={e} data-testid={`email-chip-${e}`}>
                                <S.ChipRemove
                                  type="button"
                                  onClick={() => handleRemoveEmail(e)}
                                  aria-label={`Remove ${e}`}
                                  data-testid={`remove-email-${e}`}
                                >
                                  <CloseOutlined aria-hidden />
                                </S.ChipRemove>
                                <S.ChipEmailText>{e}</S.ChipEmailText>
                              </S.Chip>
                            ))}
                          </S.ChipList>
                        )}
                      </S.EmailsToNotifySection>
                    )}
                  </React.Fragment>
                );
              }
            )}
          </S.RadioGroup>

          {showTellUsMore && (
            <S.TellUsMoreSection>
              <S.Label htmlFor="tell-us-more-input">{t('unsubscribe.tellUsMoreLabel')}</S.Label>
              <S.AddEmailInput
                id="tell-us-more-input"
                type="text"
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                data-testid="tell-us-more-input"
                $hasErrorIcon={false}
              />
            </S.TellUsMoreSection>
          )}
          {error && (
            <S.ErrorText data-testid="unsubscribe-error">{error}</S.ErrorText>
          )}

          <S.DividerLine />

          <S.ButtonWrapper>
            <Tooltip
              title={isDisabled ? t('unsubscribe.tooltip') : ''}
              disabled={!isDisabled}
            >
              <S.StyledConfirmButton>
                <Button
                  label={loading ? t('unsubscribe.processing') : t('unsubscribe.confirmButton')}
                  variant="primary"
                  onClick={handleConfirm}
                  disabled={isDisabled}
                  data-testid="unsubscribe-confirm-button"
                />
              </S.StyledConfirmButton>
            </Tooltip>
          </S.ButtonWrapper>
        </S.ReasonCard>
      </S.Card>
    </S.Container>
  );
};
